import { useMemo } from 'react';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  getTypeOrNull,
  useSurfacesExclusivityGroupsQuery,
} from '@spotify/confidence-graphql';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface SurfaceItem {
  name: string;
  displayName?: string;
}

const getSurfaces = (value: unknown): SurfaceItem[] => {
  if (!value) {
    return [];
  }

  // Handle single string (e.g., "surfaces/...")
  if (typeof value === 'string') {
    return [{ name: value }];
  }

  // Handle array
  if (!Array.isArray(value)) {
    return [];
  }

  const surfaces: SurfaceItem[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      surfaces.push({ name: item });
    } else if (
      typeof item === 'object' &&
      item !== null &&
      'name' in item &&
      typeof item.name === 'string'
    ) {
      const displayName =
        'displayName' in item && typeof item.displayName === 'string'
          ? item.displayName
          : undefined;
      surfaces.push({ name: item.name, displayName });
    }
  }

  return surfaces;
};

const surfacePrefix = (count: number) => {
  return count === 1 ? 'surface' : 'surfaces';
};

// Fallback display name logic (without GraphQL enrichment)
const getFallbackDisplayName = (surface: SurfaceItem): string => {
  return (
    surface.displayName ??
    extractLastNameComponent(surface.name) ??
    surface.name
  );
};

interface SurfaceChanges {
  oldSurfaces: SurfaceItem[];
  newSurfaces: SurfaceItem[];
  added: SurfaceItem[];
  removed: SurfaceItem[];
}

const analyzeSurfaceChanges = (change: {
  oldValue?: unknown;
  newValue?: unknown;
}): SurfaceChanges => {
  const oldSurfaces = getSurfaces(change.oldValue);
  const newSurfaces = getSurfaces(change.newValue);

  const added = newSurfaces.filter(
    n => !oldSurfaces.find(o => o.name === n.name),
  );
  const removed = oldSurfaces.filter(
    o => !newSurfaces.find(n => n.name === o.name),
  );

  return { oldSurfaces, newSurfaces, added, removed };
};

const SurfacesChangeRender = ({ change }: ActivityChangeOptions) => {
  const { added, removed } = analyzeSurfaceChanges(change);

  const filterString = [
    ...new Set([...added.map(s => s.name), ...removed.map(s => s.name)]),
  ]
    .map(name => `name:"${name}"`)
    .join(' OR ');

  const { data } = useSurfacesExclusivityGroupsQuery({
    variables: {
      filter: filterString,
      showDeletedHoldbacks: false,
      showDeletedExclusivityGroups: false,
    },
    skip: !filterString,
    fetchPolicy: 'cache-first',
  });

  const displayNameMap = useMemo(() => {
    const liveSurfaces = getTypeOrNull(data?.surfaces)?.surfaces ?? [];
    return new Map(liveSurfaces.map(s => [s.name, s.displayName]));
  }, [data]);

  const getDisplayName = (surface: SurfaceItem): string => {
    return (
      displayNameMap.get(surface.name) ??
      surface.displayName ??
      extractLastNameComponent(surface.name) ??
      surface.name
    );
  };

  // Skip rendering when single-item (handled by tag field)
  if (
    (added.length === 1 && removed.length === 0) ||
    (removed.length === 1 && added.length === 0)
  ) {
    return null;
  }

  // For multiple changes, show sections
  return (
    <>
      {added.length > 0 && (
        <ActivityItem.Section label="Added surfaces">
          {added.map(surface => (
            <ActivityTagItem
              key={surface.name}
              tagLabel={getDisplayName(surface)}
              newValue=""
            />
          ))}
        </ActivityItem.Section>
      )}

      {removed.length > 0 && (
        <ActivityItem.Section label="Removed surfaces">
          {removed.map(surface => (
            <ActivityTagItem
              key={surface.name}
              tagLabel={getDisplayName(surface)}
              oldValue=""
            />
          ))}
        </ActivityItem.Section>
      )}
    </>
  );
};

export const SURFACES_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'surfaces-change',
  predicate: ({ change }) => {
    if (change.fieldPath !== '/surfaces') {
      return false;
    }

    const { added, removed } = analyzeSurfaceChanges(change);
    return added.length > 0 || removed.length > 0;
  },
  title: change => {
    const { added, removed } = analyzeSurfaceChanges(change);

    if (added.length > 0 && removed.length === 0) {
      return `Added ${added.length} ${surfacePrefix(added.length)}`;
    }

    if (removed.length > 0 && added.length === 0) {
      return `Removed ${removed.length} ${surfacePrefix(removed.length)}`;
    }

    return 'Updated surfaces';
  },
  tag: change => {
    const { added, removed } = analyzeSurfaceChanges(change);

    if (added.length === 1 && removed.length === 0) {
      return {
        tagLabel: getFallbackDisplayName(added[0]),
        ariaLabel: 'Surface name',
      };
    }

    if (removed.length === 1 && added.length === 0) {
      return {
        tagLabel: getFallbackDisplayName(removed[0]),
        ariaLabel: 'Surface name',
      };
    }

    return null;
  },
  render: SurfacesChangeRender,
});
